import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { nanoid } from "nanoid";
import { gql } from "../../../__generated__";
import TransitionWrapper from "../../../components/common/transition-wrapper";
import FadeInWrapper from "../../../components/common/fade-in-wrapper";
import KakaoLogo from "../../../assets/images/kakao-logo.png";
import NaverLogo from "../../../assets/images/naver-logo.png";
import { formatPhoneNumber } from "../../../utils/phone";
import { track } from "@amplitude/analytics-browser";
import { GET_PACK_WITH_AVAILABLE_QUANTITY_TO_ORDER } from "../../../api/store-queries";
import { AVAILABLE_USER_COUPONS_QUERY } from "../../live-order/checkout/page";
import { UserCoupon } from "../../../__generated__/graphql";
import { ME_QUERY } from "../../../api/user-queries";
import { notifyError } from "../../../utils/toast";
import BACK_IMG from "../../../components/common/back-img.png";
import PaymentLoading from "../../../components/payment/payment-loading";
import BottomSheet from "../../../components/common/bottom-sheet";
import CouponSelectBottomSheet from "../../../components/checkout/coupon-select-bottom-sheet";
import { isPackPickupUsingToss } from "../../../utils/payment-pg-cutoff";

const CREATE_PACK_ORDER = gql(`
  mutation CreatePackOrder($input: CreatePackOrderInput!) {
    createPackOrder(input: $input) {
      id
      orderId
      amount
      sellerAmount
      coupon
      products {
        id
        name
      }
      createdAt
      updatedAt
    }
  }
`);

const CheckoutPreOrder = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const [searchParams] = useSearchParams();
  const isTomorrow = searchParams.get("isTomorrow");
  const storeIdFromSearch = searchParams.get("storeId");
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery(
    GET_PACK_WITH_AVAILABLE_QUANTITY_TO_ORDER,
    {
      variables: { storeId: Number(storeId) },
    }
  );
  const { data: userData, loading: userLoading } = useQuery(ME_QUERY, {
    fetchPolicy: "no-cache",
  });
  const { data: availableUserCouponsData } = useQuery(
    AVAILABLE_USER_COUPONS_QUERY
  );
  const [createPackOrder] = useMutation(CREATE_PACK_ORDER);
  const userPhone = userData?.me?.phone;
  const [quantity, setQuantity] = React.useState(1);
  const [selectedPayment, setSelectedPayment] = React.useState("kakaopay");
  const containerRef = useRef<HTMLDivElement>(null);

  // 스크롤 초기화
  // 이전 페이지의 스크롤 위치가 유지되어 페이지 진입 시 콘텐츠 상단이 가려지는 문제 방지
  useEffect(() => {
    setTimeout(() => {
      containerRef.current?.scrollTo(0, 0);
      window.scrollTo(0, 0);
    }, 0);
  }, []);

  const [showAvailableQuantityToOrder, setShowAvailableQuantityToOrder] =
    React.useState(false);
  const increaseQuantity = () => {
    if (
      !loading &&
      data?.pack?.availableQuantityToOrder &&
      data?.pack?.availableQuantityToOrder - quantity <= 0
    ) {
      notifyError("오늘 인당 최대 주문 수량을 초과했습니다.");
      setShowAvailableQuantityToOrder(true);
      return;
    }
    if (quantity < Number(data?.pack?.quantity)) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handlePhoneChange = () => {
    // MEMO: 휴대폰 인증번호 페이지 이동 후 리다이렉트 처리를 위해 경로와 파라미터 전체를 저장합니다
    // 예시: /pre-order/checkout/123?isTomorrow=true 에서 isTomorrow=true와 같이 쿼리스트링을 저장하지 않아서,
    // 내일 예약구매 -> 휴대폰 인증 -> 오늘 예약구매로 이어지는 경우가 있었습니다.
    localStorage.setItem(
      "redirect",
      window.location.pathname + window.location.search
    );
    navigate("/phone-number-auth?allowBack=true");
  };

  const handleProceedClick = () => {
    track(
      usesTossForPack
        ? "팩 결제하기(토스 결제 진행) 버튼 클릭"
        : "팩 결제하기(나이스페이 호출) 버튼 클릭",
      {
        storeId: storeId,
        총액: quantity * Number(data?.pack?.userPrice),
        총수량: quantity,
      },
    );
    if (!userPhone) {
      alert("연락처를 입력해주세요!");
      return;
    }

    // storeId가 58이면 바텀시트를 먼저 띄움
    if (storeIdFromSearch === "58" || storeId === "58") {
      setIsNoticeBottomSheetOpen(true);
      return;
    }

    setIsPaymentLoading(true);
    onClickProceed();
  };

  const handleConfirmNotice = () => {
    setIsNoticeBottomSheetOpen(false);
    setIsPaymentLoading(true);
    onClickProceed();
  };

  const getPackPickupWindow = () => {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayDayOfWeek = ((today.getDay() + 6) % 7).toString();
    const tomorrowDayOfWeek = ((today.getDay() + 7) % 7).toString();

    const todayPickUpTime =
      data?.pack?.store?.reserveTime[todayDayOfWeek]?.start;
    const todayPickupEndTime =
      data?.pack?.store?.reserveTime[todayDayOfWeek]?.end;
    const tomorrowPickUpTime =
      data?.pack?.store?.reserveTime[tomorrowDayOfWeek]?.start;
    const tomorrowPickupEndTime =
      data?.pack?.store?.reserveTime[tomorrowDayOfWeek]?.end;

    if (isTomorrow === "true") {
      if (!tomorrowPickUpTime || !tomorrowPickupEndTime) {
        return null;
      }

      const [tomorrowHour, tomorrowMinute] = tomorrowPickUpTime
        .split(":")
        .map(Number);
      const [tomorrowEndHour, tomorrowEndMinute] = tomorrowPickupEndTime
        .split(":")
        .map(Number);

      return {
        pickupStart: new Date(
          tomorrow.getFullYear(),
          tomorrow.getMonth(),
          tomorrow.getDate(),
          tomorrowHour,
          tomorrowMinute,
        ),
        pickupEnd: new Date(
          tomorrow.getFullYear(),
          tomorrow.getMonth(),
          tomorrow.getDate(),
          tomorrowEndHour,
          tomorrowEndMinute,
        ),
      };
    }

    if (!todayPickUpTime || !todayPickupEndTime) {
      return null;
    }

    const [hour, minute] = todayPickUpTime.split(":").map(Number);
    const [endHour, endMinute] = todayPickupEndTime.split(":").map(Number);

    return {
      pickupStart: new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        hour,
        minute,
      ),
      pickupEnd: new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        endHour,
        endMinute,
      ),
    };
  };

  const packPickupWindow = getPackPickupWindow();
  const usesTossForPack =
    packPickupWindow != null
      ? isPackPickupUsingToss(packPickupWindow.pickupStart)
      : false;

  const onClickProceed = async () => {
    try {
      if (isTomorrow !== "true") {
        const { data: latest } = await refetch();
        if (latest?.pack?.isOverTodayPickupTime) {
          if (latest.pack.isTomorrowEnabled) {
            notifyError(
              "오늘 예약이 마감되어 내일 픽업으로 변경했어요. 다시 확인 후 결제해주세요."
            );
            navigate(`/pre-order/checkout/${storeId}?isTomorrow=true`, {
              replace: true,
            });
          } else {
            notifyError("오늘 예약이 마감되었어요.");
          }
          return;
        }
      }

      if (packPickupWindow == null) {
        notifyError("픽업 시간을 불러올 수 없습니다.");
        return;
      }

      const packId = data?.pack?.id;
      if (packId == null) {
        notifyError("팩 정보를 불러올 수 없습니다.");
        return;
      }

      const orderId = nanoid();
      const version = window.location.origin.includes("mahi.co.kr") ? "2" : "1";
      const packVariant = isTomorrow === "true" ? "tomorrow" : "today";
      const payAmount =
        quantity * Number(data?.pack?.userPrice) - getCouponDiscountedAmount();

      await createPackOrder({
        variables: {
          input: {
            totalQuantity: quantity,
            totalDiscount:
              quantity *
              (Number(data?.pack?.price) - Number(data?.pack?.userPrice)),
            orderId,
            amount: payAmount,
            couponDiscountAmount: getCouponDiscountedAmount(),
            userCouponId: selectedCoupon?.id,
            baseAmount: quantity * Number(data?.pack?.userPrice),
            sellerAmount: quantity * Number(data?.pack?.discountPrice),
            productIds: [],
            packPickupStartTime: packPickupWindow.pickupStart,
            packPickupEndTime: packPickupWindow.pickupEnd,
            isPack: true,
            coupon: selectedCoupon?.coupon.title,
            storeId: Number(storeId),
            ...(usesTossForPack
              ? {
                  pendingPaymentPayload: {
                    pack: { id: packId },
                    quantity,
                    version,
                    packVariant,
                  },
                }
              : {}),
          },
        },
      });

      const goodsName = `${data?.pack?.store?.title ?? ""}랜덤팩`;
      if (usesTossForPack) {
        const intent = isTomorrow === "true" ? "pack-tomorrow" : "pack-today";
        navigate(
          `/payment/toss/widget?intent=${encodeURIComponent(
            intent,
          )}&orderId=${encodeURIComponent(orderId)}&amount=${encodeURIComponent(
            String(payAmount),
          )}&orderName=${encodeURIComponent(goodsName)}`,
        );
        return;
      }

      if (!window.AUTHNICE) {
        throw new Error("결제 모듈을 불러올 수 없습니다.");
      }

      const clientId =
        process.env.REACT_APP_NODE_ENV === "dev"
          ? process.env.REACT_APP_NICE_PAY_CLIENT_KEY_TEST
          : version === "2"
            ? process.env.REACT_APP_NICE_PAY_CLIENT_KEY_V2
            : process.env.REACT_APP_NICE_PAY_CLIENT_KEY;

      const method =
        selectedPayment === "card"
          ? "card"
          : selectedPayment === "kakaopay"
            ? "kakaopay"
            : "naverpayCard";

      window.AUTHNICE.requestPay({
        clientId,
        method,
        orderId,
        amount: payAmount,
        appScheme: "mahi://",
        fnError: () => {
          setIsPaymentLoading(false);
          navigate("/nice-result?ok=0", { replace: true });
        },
        goodsName,
        returnUrl: `${process.env.REACT_APP_API_URL}/${
          isTomorrow === "true"
            ? "nice-auth-for-tomorrow-pack"
            : "nice-auth-for-pack"
        }`,
        mallReserved: JSON.stringify({
          pack: { id: packId },
          quantity,
          version,
        }),
      });
    } catch (error) {
      notifyError(
        error instanceof Error ? error.message : "주문 생성에 실패했습니다.",
      );
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const [selectedCoupon, setSelectedCoupon] = useState<UserCoupon | null>(null);
  const [isCouponSheetOpen, setIsCouponSheetOpen] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [isNoticeBottomSheetOpen, setIsNoticeBottomSheetOpen] = useState(false);

  const isCouponDisabled = (userCoupon: UserCoupon) => {
    const minOrderPrice = userCoupon.coupon.minOrderPrice;
    if (!minOrderPrice) return false;
    return quantity * Number(data?.pack?.userPrice) < minOrderPrice;
  };

  // 매장 전용 쿠폰 필터링
  const currentStoreId = Number(storeId);
  const filteredCoupons =
    availableUserCouponsData?.availableUserCoupons?.filter(
      (userCoupon: UserCoupon) => {
        // storeId가 없으면 전체 전용 쿠폰
        if (!userCoupon.coupon.storeId) {
          return true;
        }
        // storeId가 있으면 현재 매장과 일치하는지 확인
        return userCoupon.coupon.storeId === currentStoreId;
      }
    );

  const getCouponDiscountedAmount = () => {
    if (!selectedCoupon) return 0;

    if (selectedCoupon.coupon.discountType === "PERCENT") {
      return (
        quantity *
        Number(data?.pack?.userPrice) *
        (selectedCoupon.coupon.discountValue / 100)
      );
    } else {
      return selectedCoupon.coupon.discountValue;
    }
  };

  const finalAmount =
    quantity * Number(data?.pack?.userPrice) - getCouponDiscountedAmount();

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;

  return (
    <div
      ref={containerRef}
      className="bg-white p-4 flex flex-col h-screen overflow-y-auto relative"
    >
      <FadeInWrapper className="flex flex-col pb-44">
        <img
          src={BACK_IMG}
          onClick={() => navigate(-1)}
          className="w-[40px] h-[40px] mb-4 cursor-pointer"
          alt="back"
        />
        <h1 className="text-3xl text-black font-extrabold mb-4">주문 하기</h1>

        <div className="flex flex-col space-y-8">
          {/* 주문 정보 */}
          <div>
            <h2 className="text-lg font-bold text-black mb-2">주문 정보</h2>
            <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
              <div className="flex gap-4">
                {data?.pack?.store?.img && (
                  <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden border border-gray-100">
                    <img
                      src={data.pack.store.img}
                      alt="store"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex flex-col space-y-2 flex-grow justify-center">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-sm">매장명</span>
                    <span className="text-black font-semibold">
                      {data?.pack?.store?.title}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-sm">가격</span>
                    <span className="text-gray-400 line-through text-sm">
                      {data?.pack?.price?.toLocaleString()}원
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-sm">할인가</span>
                    <span className="text-[#1562fc] font-semibold">
                      {data?.pack?.userPrice?.toLocaleString()}원
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 수량 */}
          <div>
            <h2 className="text-lg font-bold text-black mb-2">수량</h2>
            <div className="flex flex-col items-start">
              <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-1 shadow-sm h-12 w-32">
                <button
                  onClick={decreaseQuantity}
                  className="w-10 h-full flex items-center justify-center text-gray-500 hover:text-black hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <span className="text-xl">-</span>
                </button>
                <span className="text-black text-lg">{quantity}</span>
                <button
                  onClick={increaseQuantity}
                  className="w-10 h-full flex items-center justify-center text-gray-500 hover:text-black hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <span className="text-xl">+</span>
                </button>
              </div>
              {showAvailableQuantityToOrder && (
                <div className="mt-2 text-[#1562fc] text-sm">
                  남은 구매가능 개수: {data?.pack?.availableQuantityToOrder}개
                </div>
              )}
            </div>
          </div>

          {/* 연락처 */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-bold text-black">연락처</h2>
              <button
                onClick={userLoading ? undefined : handlePhoneChange}
                className={`text-sm text-gray-500 underline ${
                  userLoading ? "cursor-not-allowed opacity-50" : ""
                }`}
              >
                {userPhone ? "변경" : "등록"}
              </button>
            </div>
            <div
              className={`w-full rounded-xl px-4 h-12 flex items-center justify-center border ${
                userPhone
                  ? "bg-white border-gray-200"
                  : "bg-red-50 border-red-200"
              } shadow-sm`}
            >
              <div
                className={`text-base font-medium text-center ${
                  userPhone ? "text-black" : "text-red-500"
                }`}
              >
                {userLoading
                  ? "로딩 중..."
                  : userPhone
                  ? formatPhoneNumber(userPhone)
                  : "연락처를 등록해주세요"}
              </div>
            </div>
          </div>

          {/* 쿠폰 */}
          <div>
            <h2 className="text-lg font-bold text-black mb-2">쿠폰</h2>
            <button
              type="button"
              onClick={() => setIsCouponSheetOpen(true)}
              className="w-full px-4 min-h-12 py-3 bg-white border border-gray-200 rounded-xl shadow-sm text-left flex items-center justify-between gap-3 focus:outline-none focus:ring-2 focus:ring-[#1562fc] focus:border-transparent"
            >
              <div className="min-w-0">
                {selectedCoupon ? (
                  <>
                    <p className="text-black text-base font-medium truncate">
                      {selectedCoupon.coupon.title}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {selectedCoupon.coupon.discountType === "PERCENT"
                        ? `${selectedCoupon.coupon.discountValue}% 할인`
                        : `${Number(selectedCoupon.coupon.discountValue).toLocaleString("ko-KR")}원 할인`}
                      {selectedCoupon.expiredAt
                        ? ` · ${new Date(selectedCoupon.expiredAt).toLocaleDateString("ko-KR")}까지`
                        : ""}
                    </p>
                  </>
                ) : (
                  <p className="text-gray-500 text-base">사용할 쿠폰 선택</p>
                )}
              </div>
              <span className="text-gray-400 text-sm shrink-0">선택</span>
            </button>
          </div>

          {/* 결제수단 */}
          <div>
            <h2 className="text-lg font-bold text-black mb-2">결제수단</h2>
            {usesTossForPack ? (
              <p className="text-sm text-gray-600 rounded-xl border border-gray-200 p-4 bg-gray-50">
                다음 화면에서 카드·간편결제 등 결제 수단을 선택할 수 있어요.
              </p>
            ) : (
              <div className="flex flex-col space-y-3">
                <label
                  className={`flex items-center space-x-3 p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedPayment === "kakaopay"
                      ? "bg-yellow-50 border-yellow-400"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value="kakaopay"
                    checked={selectedPayment === "kakaopay"}
                    onChange={(event) => setSelectedPayment(event.target.value)}
                    className="w-5 h-5 accent-yellow-400"
                  />
                  <div className="flex items-center space-x-2">
                    <img src={KakaoLogo} alt="카카오페이" className="h-6" />
                    <span className="text-black font-medium">카카오페이</span>
                  </div>
                </label>

                <label
                  className={`flex items-center space-x-3 p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedPayment === "naverpay"
                      ? "bg-green-50 border-green-500"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value="naverpay"
                    checked={selectedPayment === "naverpay"}
                    onChange={(event) => setSelectedPayment(event.target.value)}
                    className="w-5 h-5 accent-green-600"
                  />
                  <div className="flex items-center space-x-2">
                    <img
                      src={NaverLogo}
                      alt="네이버페이"
                      className="h-6 rounded-lg"
                    />
                    <span className="text-black font-medium">네이버페이</span>
                  </div>
                </label>

                <label
                  className={`flex items-center space-x-3 p-4 rounded-xl border cursor-pointer transition-all ${
                    selectedPayment === "card"
                      ? "bg-blue-50 border-blue-500"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={selectedPayment === "card"}
                    onChange={(event) => setSelectedPayment(event.target.value)}
                    className="w-5 h-5 accent-blue-600"
                  />
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-6 h-6 text-gray-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3z"
                      />
                    </svg>
                    <span className="text-black font-medium">신용/체크카드</span>
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>
      </FadeInWrapper>

      {/* Bottom Area */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 pb-8 z-20">
        <div className="max-w-xl mx-auto">
          {selectedCoupon && (
            <div className="flex justify-between items-center mb-1">
              <span className="text-gray-500 text-sm">할인 금액</span>
              <span className="text-red-500 font-bold">
                -{getCouponDiscountedAmount().toLocaleString()}원
              </span>
            </div>
          )}
          <div className="flex justify-between items-center mb-4">
            <span className="text-lg font-bold text-black">총 결제 금액</span>
            <span className="text-2xl font-extrabold text-[#1562fc]">
              {finalAmount.toLocaleString()}원
            </span>
          </div>

          <TransitionWrapper
            scale={0.95}
            opacity={0.8}
            onClick={
              userLoading
                ? undefined
                : data?.pack?.isOverMaxQuantity
                ? () => {
                    notifyError(
                      "오늘 인당 최대 주문 수량을 초과했습니다. (최대 " +
                        data?.pack?.maxOrderQuantity +
                        "개)"
                    );
                  }
                : handleProceedClick
            }
            className={`w-full h-[56px] flex items-center justify-center bg-[#1562fc] rounded-2xl shadow-lg shadow-blue-500/30 ${
              userLoading || data?.pack?.isOverMaxQuantity
                ? "opacity-50 cursor-not-allowed shadow-none bg-gray-400"
                : ""
            }`}
          >
            <span className="text-white text-lg font-bold">결제하기</span>
          </TransitionWrapper>
        </div>
      </div>

      <PaymentLoading isVisible={isPaymentLoading} />
      <CouponSelectBottomSheet
        isOpen={isCouponSheetOpen}
        onClose={() => setIsCouponSheetOpen(false)}
        coupons={filteredCoupons ?? []}
        selectedCouponId={selectedCoupon?.id ?? null}
        onSelect={setSelectedCoupon}
        isCouponDisabled={isCouponDisabled}
      />
      <BottomSheet
        isOpen={isNoticeBottomSheetOpen}
        onClose={() => setIsNoticeBottomSheetOpen(false)}
        mainText="마지막으로! 🙏"
        buttonText="확인"
        onButtonClick={handleConfirmNotice}
      >
        <div className="mt-4 p-2">
          <p className="text-gray-700 text-base leading-relaxed">
            랜덤팩은 사장님이 준비한 구성 그대로 즐겨주세요!
            <br />
            <br />
            <b>메뉴 변경 요청</b>은 <b>사장님의 운영부담</b>으로 이어져 어려움을
            겪을 수 있어요!
            <br />
            <br />
            준비된 구성 그대로 즐겨주시면 감사하겠습니다! 🙇‍♂️
          </p>
        </div>
      </BottomSheet>
    </div>
  );
};

export default CheckoutPreOrder;
